import React, { ReactElement, useMemo, useEffect } from 'react';

import { TChildWrapperProps } from '@react/types';
import { BUILD_EVENT } from '@core';
import { EEVents } from '@core/constants';
import { useForceUpdate } from '@react/useForceUpdate';
import { getFormInstance } from '@core/managers';

const Field = ({
  children,
  component,
  wrapper,
  propsMapping = {},
  formId,
  onMount,
  onChange,
  onFocus,
  onBlur,
  onClick,
  onRehydrate,
  afterApiCall,
}: TChildWrapperProps): ReactElement | null => {
  const [forceChange, dispatchForceChange] = useForceUpdate();
  const [forceRehydrate, dispatchForceRehydrate] = useForceUpdate();
  const [forceBlur, dispatchForceBlur] = useForceUpdate();
  const [forceFocus, dispatchForceFocus] = useForceUpdate();
  const [forceClick, dispatchForceClick] = useForceUpdate();
  const [forceAfterApiCall, dispatchForceAfterApiCall] = useForceUpdate();
  const fieldInstance = useMemo(
    () => getFormInstance(formId).getFieldInstance(component, propsMapping),
    [],
  );
  const Element = useMemo(() => wrapper, [wrapper]);

  useMemo(() => {
    fieldInstance.publish(BUILD_EVENT(EEVents.ON_FIELD_MOUNT, component.name));
    onMount(fieldInstance.data);
  }, []);

  // Effects to make the Field -> Form callback flow synchronous

  useEffect(() => {
    if (!forceRehydrate) return;
    onRehydrate(fieldInstance.data);
  }, [forceRehydrate]);

  useEffect(() => {
    if (!forceChange) return;
    onChange(fieldInstance.data);
  }, [forceChange]);

  useEffect(() => {
    if (!forceBlur) return;
    onBlur(fieldInstance.data);
  }, [forceBlur]);

  useEffect(() => {
    if (!forceFocus) return;
    onFocus(fieldInstance.data);
  }, [forceFocus]);

  useEffect(() => {
    if (!forceClick) return;
    onClick(fieldInstance.data);
  }, [forceClick]);

  useEffect(() => {
    if (!forceAfterApiCall) return;
    afterApiCall(fieldInstance.data);
  }, [forceAfterApiCall]);

  //Effects to listen to form Events
  useEffect(
    () =>
      fieldInstance.subscribe(
        BUILD_EVENT(EEVents.ON_FIELD_REHYDRATE, component.name),
        () => dispatchForceRehydrate(),
      ),
    [],
  );

  useEffect(
    () =>
      fieldInstance.subscribe(
        BUILD_EVENT(EEVents.ON_FIELD_BLUR, component.name),
        () => dispatchForceBlur(),
      ),
    [],
  );

  useEffect(
    () =>
      fieldInstance.subscribe(
        BUILD_EVENT(EEVents.ON_FIELD_CHANGE, component.name),
        () => dispatchForceChange(),
      ),
    [],
  );

  useEffect(
    () =>
      fieldInstance.subscribe(
        BUILD_EVENT(EEVents.ON_FIELD_FOCUS, component.name),
        () => dispatchForceFocus(),
      ),
    [],
  );

  useEffect(
    () =>
      fieldInstance.subscribe(
        BUILD_EVENT(EEVents.ON_FIELD_CLICK, component.name),
        () => dispatchForceClick(),
      ),
    [],
  );

  useEffect(
    () =>
      fieldInstance.subscribe(
        BUILD_EVENT(EEVents.AFTER_FIELD_API_CALL, component.name),
        () => dispatchForceAfterApiCall(),
      ),
    [],
  );

  if (!fieldInstance.data.visible || !fieldInstance.data.mounted) {
    return null;
  }
  return <Element {...fieldInstance.props}>{children}</Element>;
};

export default Field;
